import { useState } from "react";
import { Play, ChevronDown, ChevronUp } from "lucide-react";

// Optional R2-hostad guide-film. Sätts via REACT_APP_ANSLAG_GUIDE_VIDEO_URL.
// Filen ska INTE checkas in i repo (för stor). Om env-variabeln saknas används
// fallback-URL:en mot vår publika R2-bucket (media.vidalv.live).
const DEFAULT_VIDEO_URL = "https://media.vidalv.live/videos/skicka-anslag.mp4";
const VIDEO_URL = (process.env.REACT_APP_ANSLAG_GUIDE_VIDEO_URL || DEFAULT_VIDEO_URL).trim();
const POSTER_URL = (process.env.REACT_APP_ANSLAG_GUIDE_POSTER_URL || "").trim();

const STEPS = [
  "Gå till formuläret.",
  "Välj vad anslaget handlar om.",
  "Skriv några enkla rader.",
  "Lägg till en bild om du vill.",
  "Kontrollera dina uppgifter.",
  "Skicka in anslaget.",
  "Redaktionen tittar på anslaget innan det publiceras.",
];

export default function AnslagGuideBox() {
  const [open, setOpen] = useState(false);
  const hasVideo = !!VIDEO_URL;

  return (
    <section
      data-testid="anslag-guide-box"
      className="mb-8 sm:mb-10 bg-parchment border border-warm-border rounded-sm p-5 sm:p-6"
    >
      <h2
        data-testid="anslag-guide-title"
        className="font-heading text-lg sm:text-xl font-semibold text-ink tracking-tight mb-1.5"
      >
        Så skickar du in ett anslag
      </h2>
      <p className="text-sm text-bark/70 leading-relaxed mb-4">
        Du kan fylla i formuläret direkt. Om du är osäker kan du först titta på den korta guiden.
      </p>

      <ol
        data-testid="anslag-guide-steps"
        className="list-decimal pl-5 flex flex-col gap-1.5 text-sm sm:text-base text-bark leading-relaxed marker:text-pine/70 marker:font-semibold"
      >
        {STEPS.map((s, i) => (
          <li key={i} data-testid={`anslag-guide-step-${i}`}>{s}</li>
        ))}
      </ol>

      <div className="mt-5 pt-4 border-t border-warm-border/60">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="anslag-guide-toggle"
          aria-expanded={open}
          aria-controls="anslag-guide-video-region"
          className="inline-flex items-center gap-2 text-sm font-semibold text-falu hover:text-falu-hover bg-transparent transition-colors"
        >
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <Play size={14} />
          Visa kort guide
        </button>

        {open && (
          <div
            id="anslag-guide-video-region"
            data-testid="anslag-guide-video-region"
            className="mt-4"
          >
            {hasVideo ? (
              <>
                <video
                  data-testid="anslag-guide-video"
                  src={VIDEO_URL}
                  poster={POSTER_URL || undefined}
                  controls
                  preload="metadata"
                  playsInline
                  className="w-full max-w-2xl rounded-sm border border-warm-border bg-ink"
                >
                  Din webbläsare stöder inte inbäddad video. Du kan ändå fylla i formuläret nedan.
                </video>
                <p className="text-xs text-bark/60 mt-2">
                  Filmen är frivillig. Du kan också fortsätta direkt med formuläret nedan.
                </p>
              </>
            ) : (
              <p
                data-testid="anslag-guide-video-fallback"
                className="text-sm text-bark/70 bg-bone-alt border border-warm-border rounded-sm px-3 py-2.5"
              >
                En kort instruktionsfilm kommer snart. Du kan ändå fylla i formuläret nedan.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
